/**
 * Base UI kit. Brand rules: Moss for primary actions, Terra Deep for the rare
 * accent action, white cards on a cream page, Baloo 2 for headings.
 */
import { forwardRef, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "../lib/cn";

/* ------------------------------------------------------------------ */
/* Buttons                                                             */
/* ------------------------------------------------------------------ */

type Variant = "primary" | "accent" | "secondary" | "ghost" | "danger" | "link";
type Size = "sm" | "md";

const VARIANTS: Record<Variant, string> = {
  primary: "bg-moss text-paper hover:bg-moss-soft shadow-sm",
  accent: "bg-terra-deep text-paper hover:bg-terra-text shadow-sm",
  secondary: "bg-paper text-moss border border-line hover:border-sage-300 hover:bg-sage-50",
  ghost: "text-olive hover:bg-sage-50",
  danger: "bg-paper text-danger-ink border border-danger/30 hover:bg-danger-bg",
  link: "text-olive underline decoration-sage-200 underline-offset-4 hover:decoration-olive px-0! h-auto!",
};
const SIZES: Record<Size, string> = {
  sm: "h-8 px-3 text-[13px] gap-1.5 rounded-md",
  md: "h-10 px-4 text-sm gap-2 rounded-lg",
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  icon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "secondary", size = "md", loading, icon, className, children, disabled, type = "button", ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={cn(
        "inline-flex shrink-0 items-center justify-center font-semibold whitespace-nowrap transition-colors duration-150 select-none disabled:cursor-not-allowed disabled:opacity-50",
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : icon}
      {children}
    </button>
  );
});

export const IconButton = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & { label: string; size?: Size }>(function IconButton(
  { label, className, children, size = "md", type = "button", ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-md text-olive transition-colors hover:bg-sage-50 hover:text-moss disabled:opacity-40",
        size === "sm" ? "size-7" : "size-9",
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
});

/* ------------------------------------------------------------------ */
/* Form controls                                                       */
/* ------------------------------------------------------------------ */

const control =
  "w-full rounded-lg border border-line bg-paper px-3 text-sm text-ink placeholder:text-subtle transition-colors hover:border-cream-400 focus:border-olive focus:outline-none focus:ring-2 focus:ring-sage-100 disabled:bg-cream disabled:text-muted";

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }>(function Input(
  { className, invalid, ...rest },
  ref,
) {
  return <input ref={ref} className={cn(control, "h-10", invalid && "border-danger focus:border-danger focus:ring-danger-bg", className)} {...rest} />;
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }>(function Textarea(
  { className, invalid, rows = 3, ...rest },
  ref,
) {
  return <textarea ref={ref} rows={rows} className={cn(control, "py-2 leading-relaxed", invalid && "border-danger", className)} {...rest} />;
});

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement> & { invalid?: boolean }>(function Select(
  { className, invalid, children, ...rest },
  ref,
) {
  return (
    <select
      ref={ref}
      className={cn(
        control,
        "h-10 cursor-pointer appearance-none bg-[length:16px] bg-[right_10px_center] bg-no-repeat pr-9",
        "bg-[url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%232F461A' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")]",
        invalid && "border-danger",
        className,
      )}
      {...rest}
    >
      {children}
    </select>
  );
});

export function Checkbox({ label, checked, onChange, disabled, hint }: { label: ReactNode; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean; hint?: ReactNode }) {
  return (
    <label className={cn("flex cursor-pointer items-start gap-2.5 text-sm", disabled && "cursor-not-allowed opacity-60")}>
      <input
        type="checkbox"
        className="mt-0.5 size-4 shrink-0 cursor-pointer rounded accent-[#1C300A]"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>
        <span className="text-ink">{label}</span>
        {hint && <span className="block text-xs text-muted">{hint}</span>}
      </span>
    </label>
  );
}

export function Field({ label, error, hint, children, className, required }: { label: ReactNode; error?: string; hint?: ReactNode; children: ReactNode; className?: string; required?: boolean }) {
  return (
    <label className={cn("block", className)}>
      <span className="mb-1.5 block text-[13px] font-semibold text-moss">
        {label}
        {required && <span className="text-terra-text"> *</span>}
      </span>
      {children}
      {error ? <span className="mt-1 block text-xs font-semibold text-danger-ink">{error}</span> : hint ? <span className="mt-1 block text-xs text-muted">{hint}</span> : null}
    </label>
  );
}

/** A segmented control for 2–4 options. */
export function Segmented<T extends string>({ value, onChange, options, size = "md" }: { value: T; onChange: (v: T) => void; options: { value: T; label: ReactNode }[]; size?: Size }) {
  return (
    <div role="radiogroup" className={cn("inline-flex rounded-lg border border-line bg-cream p-0.5", size === "sm" ? "text-[12px]" : "text-[13px]")}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          onClick={() => onChange(o.value)}
          className={cn(
            "rounded-md px-3 font-semibold transition-colors",
            size === "sm" ? "h-7" : "h-8",
            value === o.value ? "bg-paper text-moss shadow-sm" : "text-muted hover:text-moss",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Surfaces                                                            */
/* ------------------------------------------------------------------ */

export function Card({ children, className, padded = true }: { children: ReactNode; className?: string; padded?: boolean }) {
  return <section className={cn("rounded-[var(--radius-card)] border border-line-soft bg-paper shadow-[var(--shadow-card)]", padded && "p-5", className)}>{children}</section>;
}

export function CardHeader({ title, eyebrow, actions, className, children }: { title: ReactNode; eyebrow?: ReactNode; actions?: ReactNode; className?: string; children?: ReactNode }) {
  return (
    <div className={cn("mb-4 flex items-start justify-between gap-3", className)}>
      <div className="min-w-0">
        {eyebrow && <div className="eyebrow mb-0.5">{eyebrow}</div>}
        <h3 className="text-lg font-semibold">{title}</h3>
        {children}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Stat({ label, value, sub, tone = "default", className }: { label: ReactNode; value: ReactNode; sub?: ReactNode; tone?: "default" | "danger" | "warn" | "success"; className?: string }) {
  const toneCls = { default: "text-moss", danger: "text-danger-ink", warn: "text-terra-text", success: "text-success-ink" }[tone];
  return (
    <div className={cn("min-w-0", className)}>
      <div className="eyebrow">{label}</div>
      <div className={cn("num mt-1 font-display text-[28px] leading-none font-semibold", toneCls)}>{value}</div>
      {sub && <div className="mt-1.5 text-xs text-muted">{sub}</div>}
    </div>
  );
}

export function PageHeader({ title, eyebrow, actions, children }: { title: ReactNode; eyebrow?: ReactNode; actions?: ReactNode; children?: ReactNode }) {
  return (
    <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div className="min-w-0">
        {eyebrow && <div className="eyebrow mb-1">{eyebrow}</div>}
        <h1 className="text-[30px] font-bold tracking-tight">{title}</h1>
        {children && <div className="mt-1 text-[15px] text-muted">{children}</div>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </header>
  );
}

export function EmptyState({ icon, title, children, action, className }: { icon?: ReactNode; title: ReactNode; children?: ReactNode; action?: ReactNode; className?: string }) {
  return (
    <div className={cn("flex flex-col items-center justify-center px-6 py-12 text-center", className)}>
      {icon && <div className="mb-3 grid size-12 place-items-center rounded-full bg-sage-50 text-olive">{icon}</div>}
      <div className="font-display text-lg font-semibold text-moss">{title}</div>
      {children && <div className="mt-1 max-w-sm text-sm text-muted">{children}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={cn("size-5 animate-spin text-sage-300", className)} aria-label="Loading" />;
}

export function Loading({ label = "Loading…" }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted">
      <Spinner /> {label}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn("animate-pulse rounded-md bg-cream-deep/70", className)} />;
}

export function ErrorNote({ error, className }: { error: unknown; className?: string }) {
  if (!error) return null;
  const msg = error instanceof Error ? error.message : String(error);
  return <div className={cn("rounded-lg border border-danger/25 bg-danger-bg px-3 py-2 text-sm text-danger-ink", className)}>{msg}</div>;
}

/** A coloured inline notice: info, warning, danger or success. */
export function Notice({ tone = "info", icon, title, children, className, action }: { tone?: "info" | "warn" | "danger" | "success"; icon?: ReactNode; title?: ReactNode; children?: ReactNode; className?: string; action?: ReactNode }) {
  const cls = {
    info: "border-info/25 bg-info-bg text-info",
    warn: "border-terra-deep/25 bg-warn-bg text-terra-text",
    danger: "border-danger/25 bg-danger-bg text-danger-ink",
    success: "border-success/25 bg-success-bg text-success-ink",
  }[tone];
  return (
    <div className={cn("flex items-start gap-2.5 rounded-lg border px-3 py-2.5 text-sm", cls, className)}>
      {icon && <span className="mt-0.5 shrink-0">{icon}</span>}
      <div className="min-w-0 flex-1">
        {title && <div className="font-bold">{title}</div>}
        {children && <div className={cn(title ? "mt-0.5" : null, "text-[13px] leading-snug")}>{children}</div>}
      </div>
      {action}
    </div>
  );
}

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="rounded border border-line bg-cream px-1.5 py-0.5 font-sans text-[11px] font-semibold text-muted">{children}</kbd>;
}

/** Definition list row used in detail panels. */
export function DetailRow({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[minmax(110px,38%)_1fr] gap-3 border-b border-line-soft py-2 text-sm last:border-0">
      <dt className="text-muted">{label}</dt>
      <dd className="min-w-0 text-ink">{children}</dd>
    </div>
  );
}

/** Horizontal progress bar. */
export function Progress({ value, max, tone = "moss", className }: { value: number; max: number; tone?: "moss" | "terra" | "danger"; className?: string }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  const bar = { moss: "bg-sage-300", terra: "bg-terra", danger: "bg-danger" }[tone];
  return (
    <div className={cn("h-1.5 w-full overflow-hidden rounded-full bg-cream-deep", className)} role="progressbar" aria-valuenow={value} aria-valuemax={max}>
      <div className={cn("h-full rounded-full transition-[width]", bar)} style={{ width: `${pct}%` }} />
    </div>
  );
}
